import { ThirdPartyDraggable } from '@fullcalendar/interaction';
import React, { useEffect } from 'react';
import { halfHour } from 'shared/constants';
import { ExtendedProps } from 'shared/lib/full-calendar';
import { DataType } from 'shared/types/data-type';
import { Task } from 'shared/types/task';
import { WeekDays } from 'shared/types/week-days';

export type Options = {
  listRef: React.RefObject<HTMLDivElement> | undefined;
  tasks: Task[];
  weekStartsOn: WeekDays;
  includeDuration?: boolean;
};

export const useRegisterFullCalendarDraggableItems = ({
  listRef,
  tasks,
  weekStartsOn,
  includeDuration,
}: Options) =>
  useEffect(() => {
    if (!listRef?.current) {
      return;
    }

    const thirdPartyDraggable = new ThirdPartyDraggable(listRef.current, {
      // eslint-disable-next-line max-len -- long selector
      itemSelector: `
       [role="treeitem"]:has([data-component-type="${DataType.Task}"]),
       [role="treeitem"]:has([data-component-type="${DataType.Goal}"]),
       [role="treeitem"]:has([data-component-type="${DataType.LifeArea}"])
     `,
      eventData: (el) => {
        const dataEl = el.querySelector('[data-component-type]');
        const id = dataEl?.getAttribute('data-id');
        const task = tasks.find((task) => task.id === id);
        const title = dataEl?.getAttribute('data-title');
        const duration = dataEl?.getAttribute('data-duration') ?? halfHour;
        const componentType = dataEl?.getAttribute(
          'data-component-type',
        ) as DataType; // Cast to DataType

        if (!id) {
          return;
        }

        // Prepare extendedProps based on the component type
        const extendedProps: ExtendedProps = {
          weekStartsOn,
          task,
        };

        switch (componentType) {
          case DataType.Task:
            extendedProps.taskId = id;
            break;
          case DataType.Goal:
            extendedProps.goalId = id;
            break;
          case DataType.LifeArea:
            extendedProps.lifeAreaId = id;
            break;
        }

        const eventBase = {
          id,
          title,
          extendedProps,
          create: true,
        };

        if (includeDuration) {
          return {
            ...eventBase,
            duration: { ms: duration },
          };
        }

        return eventBase;
      },
    });

    return () => thirdPartyDraggable?.destroy();
  }, [includeDuration, listRef, tasks, weekStartsOn]);
